import {
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Icon,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  Link,
} from "@chakra-ui/react";
import CART, { Cart as CartType } from "../../types/Cart";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import CheckOutButton from "../../molecules/CheckoutButton";
import EmptyCart from "../../molecules/EmptyCart";
import REMOVE from "../../types/Remove";
import ShoppingCartItem from "../../molecules/ShoppingCartItem";
import UPDATE from "../../types/Update";
import { useAppState } from "../../context";
import { getProductPrice } from "../../utils/shopUtils";
import debounce from "lodash.debounce";
import { MdShoppingCart } from "../../customIcons";
import useAuth from "../../useAuth";

type ShoppingCartDrawerType = {
  placement: any;
  iconColor?: string;
  hoverBg: string;
  width: string;
  height: string;
  boxWidth?: string;
  boxHeight?: string;
  border?: string;
  pt?: string;
  px?: string;
  borderRadius?: string;
  callOnClose?: () => void;
  isSmallResolutionDrawer?: boolean;
};

type CartQuery = {
  cart?: CartType;
};

const ShoppingCartDrawer: FC<ShoppingCartDrawerType> = ({
  placement,
  iconColor,
  hoverBg,
  width,
  height,
  boxWidth,
  boxHeight,
  border,
  pt,
  px,
  borderRadius,
  callOnClose,
  isSmallResolutionDrawer,
}) => {
  const { loggedIn, user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef<HTMLButtonElement>(null);

  const { cart, setCart } = useAppState();

  const [maybeGetCart, { loading }] = useLazyQuery<CartQuery>(CART, {
    onCompleted: ({ cart }) => {
      setCart(cart);
    },
    fetchPolicy: "network-only",
  });

  const [removeAllItems, { loading: removeItemLoading }] = useMutation(REMOVE, {
    onCompleted({ removeItemsFromCart: { cart } }) {
      setCart(cart);
    },
  });

  const [updateQuantities, { loading: updateQuantityLoading }] = useMutation(
    UPDATE,
    {
      onCompleted({ updateItemQuantities: { cart } }) {
        setCart(cart);
      },
    },
  );

  const debouncedUpdateQuantities = debounce((updatedQuantities: any) => {
    updateQuantities({ variables: updatedQuantities });
  }, 500);

  const handleUpdateQuantities = (updatedQuantities: any) => {
    debouncedUpdateQuantities(updatedQuantities);
  };

  const [initialLoad, setInitialLoad] = useState(true);

  const updateLoading = useMemo(() => {
    return updateQuantityLoading || removeItemLoading;
  }, [updateQuantityLoading, removeItemLoading]);

  useEffect(() => {
    if (cart || !initialLoad) {
      return;
    }

    setInitialLoad(false);
    maybeGetCart();
  }, [cart, maybeGetCart]);

  const isLoading = useMemo(() => {
    return loading || !cart;
  }, [loading, cart]);

  const cartContentNodes = useMemo(() => {
    if (!cart || !cart.contents || !cart.contents.nodes) {
      return [];
    }

    const nodes = cart.contents.nodes.filter((item) => item.product?.node);
    const databaseIdMap = new Map();

    nodes.forEach((item) => {
      const databaseId = item.product.node.databaseId;
      const isVariableProduct = item.variation?.node;

      if (!databaseId) {
        return;
      }

      if (!isVariableProduct) {
        if (databaseIdMap.has(databaseId)) {
          const existingItem = databaseIdMap.get(databaseId);

          existingItem.quantity += item.quantity;
          existingItem.gratis = Math.min(existingItem.quantity, item.quantity);
        } else {
          databaseIdMap.set(databaseId, { ...item });
        }
      } else {
        databaseIdMap.set(Symbol(), item);
      }
    });

    return Array.from(databaseIdMap.values());
  }, [cart]);

  const handleRemoveAllItems = (item: any) => {
    // Google Analytics event for removing items from the cart
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "remove_from_cart",
      user_id: loggedIn ? user?.id : "guest",
      ecommerce: {
        items: [
          {
            item_name: item.product.node.name,
            item_id: item.product.node.databaseId,
            price: item.product.node.price
              ? parseFloat(item.product.node.price?.replace("$", ""))
              : undefined,
            item_brand: item.brand,
            item_category: item.product.node.productCategories,
            quantity: item.quantity,
          },
        ],
      },
    });

    removeAllItems({
      variables: {
        input: {
          clientMutationId: `1234`,
          all: false,
          keys: item.key,
        },
      },
    });
  };

  const formatPrice = (price: string | undefined): string => {
    if (!price) return "";
    const numericPrice = parseFloat(price.replace("$", "").replace(",", ""));
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(numericPrice);
  };

  const calculatePrice = (item: any) => {
    let priceToShow = 0;

    if (item.variation?.node?.name.toLowerCase().includes("short dated")) {
      priceToShow = parseFloat(
        (item.variation.node.salePrice || item.variation.node.price)
          .replace("$", "")
          .replace(",", ""),
      );
    } else if (item.product?.node?.promotions?.onPromotion) {
      priceToShow = parseFloat(
        item.product?.node?.regularPrice.replace("$", "").replace(",", ""),
      );
    } else {
      priceToShow = getProductPrice(
        item.quantity,
        item.product?.node?.tierPrice,
        item.product?.node?.regularPrice,
      );
    }

    return priceToShow;
  };

  return (
    <>
      <Box
        m={0}
        p={0}
        width={boxWidth}
        height={boxHeight}
        border={border}
        px={px}
        borderRadius={borderRadius}
        _hover={{ background: hoverBg }}
        position="relative"
        pt={pt}
        cursor="pointer"
        onClick={onOpen}
      >
        <Icon
          aria-label="Call Sage"
          width={width}
          height={height}
          as={MdShoppingCart}
          color={iconColor}
          fill="#fff"
        />

        {cart?.contents?.itemCount ? (
          <Box
            position="absolute"
            top="0"
            right="0"
            backgroundColor="#E53E3E"
            py="2px"
            px="4px"
            borderRadius="99px"
            zIndex={99}
            minW="27px"
            transform="translate(50%, -50%)"
          >
            <Text
              fontSize="12px"
              lineHeight="16px"
              fontWeight="500"
              color="white"
              textAlign="center"
            >
              {cart.contents.itemCount}
            </Text>
          </Box>
        ) : (
          <></>
        )}
      </Box>

      <Drawer
        size="sm"
        isOpen={isOpen}
        placement={placement}
        onClose={() => {
          onClose();
          if (callOnClose) {
            callOnClose();
          }
        }}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        {isLoading ? (
          <DrawerContent>
            <Center
              alignItems="center"
              justifyContent="center"
              width="100%"
              height="100%"
            >
              <Spinner />
            </Center>
          </DrawerContent>
        ) : (
          <DrawerContent h="100%">
            <DrawerHeader alignItems="center">
              <HStack py="12px" alignItems="center">
                <MdShoppingCart fill="#CB809E" w="24px" h="24px" />
                <Text fontWeight="600" fontSize="18px">
                  My cart{" "}
                  <Text fontWeight="500" color="#4A5568" as="span">
                    ({cart?.contents.itemCount}){" "}
                  </Text>
                </Text>

                {updateLoading && (
                  <Text fontWeight="400" fontSize="14px">
                    Updating cart...
                  </Text>
                )}
              </HStack>
            </DrawerHeader>

            <DrawerCloseButton mt="20px" size="sm" />

            <DrawerBody
              paddingTop="2px"
              bg={isSmallResolutionDrawer ? "#fff" : "#f3f4f5"}
              overflowX="visible"
              overflowY="scroll"
              pb="200px"
              px="8px"
              pt="4px"
            >
              <Stack gap={1}>
                {Number(cart?.contents.itemCount) > 0 ? (
                  cartContentNodes.map((item) => {
                    const priceToShow = calculatePrice(item);
                    console.log(item);

                    return (
                      <ShoppingCartItem
                        key={item.key}
                        isDrawer
                        onClickDelete={() => handleRemoveAllItems(item)}
                        onChangeQuantity={(qty: any) => {
                          handleUpdateQuantities({
                            key: item.key,
                            quantity: qty,
                          });
                        }}
                        name={item.product.node.name}
                        variationName={item.variation?.node.name}
                        price={priceToShow}
                        quantity={item.quantity}
                        imageUrl={item.product.node.image?.sourceUrl}
                        altText={item.product.node.image?.altText}
                        activeSubstance={
                          item?.product?.node?.allPaActiveIngredient?.nodes
                        }
                        slug={item.product.node.slug}
                        gratis={item.gratis}
                        updateLoading={updateLoading}
                        salePrice={item.product.node?.salePrice}
                      />
                    );
                  })
                ) : (
                  <EmptyCart />
                )}
              </Stack>
            </DrawerBody>
            <Stack
              p="16px"
              borderTop="1px solid #E2E8F0"
              bg="#fff"
              h="188px"
              width="100%"
              bottom={0}
              position="fixed"
              gap={4}
            >
              <HStack justifyContent="space-between">
                <Text color="#4A5568">Subtotal</Text>
                <Text fontSize="18px" color="#171923" fontWeight="600">
                  {formatPrice(cart?.subtotal)}
                </Text>
              </HStack>

              <Button
                border="1px solid #3B2060"
                as={Link}
                href="/cart"
                variant="outline"
                fontSize="16px"
                color="#3B2060"
                width="100%"
                h="48px"
                fontWeight="600"
                _hover={{ bg: "#F3EBFF" }}
                isDisabled={updateLoading}
              >
                View cart
              </Button>
              <CheckOutButton
                title="Checkout"
                width="100%"
                isDisabled={updateLoading}
              />
            </Stack>
          </DrawerContent>
        )}
      </Drawer>
    </>
  );
};

export default ShoppingCartDrawer;
